import { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, Dialog, DialogActions, DialogContent, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
    CheckCircleRounded,
    DeleteOutline,
    IosShare,
    LockOutlined,
    MoreHorizRounded,
    PublicRounded,
    RadioButtonUncheckedRounded,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';

import BottomMenu from '../components/BottomMenu';
import BlurPillButton from '../components/BlurPillButton';
import IconSmall from '../components/IconSmall';
import SigninRequired from '../components/SigninRequired';
import {
    getChatV2ErrorMessage,
} from '../services/chatV2';
import { getWebChatThreads } from '../services/chatV3';
import { chatThreadKey, getChatAdapter, invalidateChatSuggestions } from '../services/chatThreads';
import { isChatDeleted, rememberDeletedChat } from '../services/deletedChats';
import { getFilteredTopics } from '../utils/chatUtils';

const PAGE_SIZE = 20;
const THREAD_ACTION = {
    DELETE: 'delete',
    SHARE: 'share',
    UNSHARE: 'unshare',
};

const getThreadTitle = (thread) => {
    const preferredTitle = String(thread && thread.title ? thread.title : '').trim();
    if (preferredTitle) {
        return preferredTitle;
    }
    return 'Untitled chat';
};

export const ChatThreads = ({ authenticated, uid }) => {
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [threadActionById, setThreadActionById] = useState({});
    const [threadMenuAnchorEl, setThreadMenuAnchorEl] = useState(null);
    const [threadMenuThreadId, setThreadMenuThreadId] = useState(null);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedThreadIds, setSelectedThreadIds] = useState(new Set());
    const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
    const [deleteSelectedInProgress, setDeleteSelectedInProgress] = useState(false);
    const loadMoreObserverRef = useRef(null);
    const requestGenerationRef = useRef(0);
    const pendingLoadRef = useRef(false);
    const hasThreads = threads.length > 0;
    const hasMore = Boolean(cursor);
    const hasActiveThreadActions = Object.keys(threadActionById).length > 0;
    const editControlsDisabled = deleteSelectedInProgress || hasActiveThreadActions;
    const selectedCount = selectedThreadIds.size;

    const tryLoadMore = useCallback((requestCursor) => {
        if (pendingLoadRef.current || !authenticated) return;
        pendingLoadRef.current = true;
        const generation = requestGenerationRef.current;
        setLoading(true);

        setErrorMessage('');

        getWebChatThreads({
            cursor: requestCursor,
            limit: PAGE_SIZE,
        })
            .then((response) => {
                if (generation !== requestGenerationRef.current) return;
                const fetchedThreads = (response && Array.isArray(response.threads) ? response.threads : [])
                    .map((thread) => ({ ...thread, key: chatThreadKey(thread) }))
                    .filter((thread) => !isChatDeleted(uid, thread.key));
                const nextCursor = response && response.cursor ? response.cursor : null;

                setThreads(previous => {
                    const keys = new Set();
                    return [...(requestCursor ? previous : []), ...fetchedThreads]
                        .filter(thread => !keys.has(thread.key) && keys.add(thread.key));
                });
                setCursor(nextCursor !== requestCursor ? nextCursor : null);
            })
            .catch((err) => {
                if (generation !== requestGenerationRef.current) return;
                console.log(err);

                if (!requestCursor) {
                    setThreads([]);
                    setCursor(null);
                }
                setErrorMessage(getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error)
                    || (requestCursor ? 'Failed to load more chats.' : 'Failed to load chat history.'));
            })
            .finally(() => {
                if (generation !== requestGenerationRef.current) return;
                pendingLoadRef.current = false;
                setLoading(false);
            });
    }, [authenticated, uid]);

    useEffect(() => {
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'chat_threads',
        });
        requestGenerationRef.current += 1;
        pendingLoadRef.current = false;
        setThreads([]);
        setSelectedThreadIds(new Set());
        setCursor(null);
        if (authenticated) tryLoadMore(null);
        return () => { requestGenerationRef.current += 1; };
    }, [analytics, authenticated, uid, tryLoadMore]);

    useEffect(() => {
        if (!loadMoreObserverRef.current || !hasMore || loading || errorMessage) {
            return;
        }

        const targetNode = loadMoreObserverRef.current;
        let active = true;
        const observer = new IntersectionObserver(
            (entries) => {
                if (active && entries.some(entry => entry.isIntersecting)) {
                    active = false;
                    observer.disconnect();
                    tryLoadMore(cursor);
                }
            },
            {
                root: null,
                rootMargin: '200px',
            },
        );

        observer.observe(targetNode);

        return () => {
            active = false;
            observer.disconnect();
        };
    }, [hasMore, cursor, loading, errorMessage, tryLoadMore]);

    useEffect(() => {
        const threadIds = new Set(threads.map((thread) => thread.key).filter(Boolean));

        setSelectedThreadIds((prevSelectedThreadIds) => {
            let changed = false;
            const nextSelectedThreadIds = new Set();

            prevSelectedThreadIds.forEach((threadId) => {
                if (threadIds.has(threadId)) {
                    nextSelectedThreadIds.add(threadId);
                } else {
                    changed = true;
                }
            });

            if (!changed && nextSelectedThreadIds.size === prevSelectedThreadIds.size) {
                return prevSelectedThreadIds;
            }

            return nextSelectedThreadIds;
        });

        if (threads.length === 0 && selectionMode && !deleteSelectedInProgress) {
            setSelectionMode(false);
        }
    }, [deleteSelectedInProgress, selectionMode, threads]);

    const handleThreadMenuClose = () => {
        setThreadMenuAnchorEl(null);
        setThreadMenuThreadId(null);
    };

    const threadClick = (thread) => () => {
        if (!thread || !thread.key) {
            return;
        }

        if (selectionMode) {
            toggleThreadSelection(thread.key);
            return;
        }

        logEvent(analytics, 'chat_thread_click', { threadId: thread.key });
        navigate(getChatAdapter(thread.engineVersion).threadPath(thread.threadId));
    };

    const getThreadTopicsText = (topics) => {
        const filteredTopics = getFilteredTopics(topics);
        return filteredTopics.slice(0, 2).join(', ');
    };

    const getThreadSharePath = (thread) => {
        if (thread && thread.sharePath) {
            return thread.sharePath;
        }

        return getChatAdapter(thread.engineVersion).publicPath(thread.threadId);
    };

    const threadForKey = (key) => {
        const thread = threads.find((item) => item.key === key);
        if (!thread) throw new Error('Conversation is no longer available.');
        return thread;
    };
    const deleteThread = async (key) => {
        const thread = threadForKey(key);
        const response = await getChatAdapter(thread.engineVersion).delete(thread.threadId);
        if (response?.error) throw new Error(getChatV2ErrorMessage(response.error));
        rememberDeletedChat(uid, key);
        invalidateChatSuggestions();
    };

    const getActiveThreadAction = (threadId) => threadActionById[threadId] || null;

    const setActiveThreadAction = (threadId, action) => {
        setThreadActionById((prevThreadActionById) => {
            if (!action) {
                if (!prevThreadActionById[threadId]) {
                    return prevThreadActionById;
                }

                const nextThreadActionById = { ...prevThreadActionById };
                delete nextThreadActionById[threadId];
                return nextThreadActionById;
            }

            return {
                ...prevThreadActionById,
                [threadId]: action,
            };
        });
    };

    const updateThread = (threadId, updater) => {
        setThreads((prevThreads) => prevThreads.map((thread) => {
            if (thread.key !== threadId) {
                return thread;
            }

            return updater(thread);
        }));
    };

    const toggleSelectionMode = () => {
        if (editControlsDisabled) {
            return;
        }

        setSelectionMode((prevSelectionMode) => {
            const nextSelectionMode = !prevSelectionMode;

            if (!nextSelectionMode) {
                setSelectedThreadIds(new Set());
            }

            return nextSelectionMode;
        });
        setConfirmAction(null);
        setShowDeleteSelectedConfirm(false);
        handleThreadMenuClose();
    };

    const toggleThreadSelection = (threadId) => {
        setSelectedThreadIds((prevSelectedThreadIds) => {
            const nextSelectedThreadIds = new Set(prevSelectedThreadIds);

            if (nextSelectedThreadIds.has(threadId)) {
                nextSelectedThreadIds.delete(threadId);
            } else {
                nextSelectedThreadIds.add(threadId);
            }

            return nextSelectedThreadIds;
        });
    };

    const openThreadMenu = (thread) => (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!thread || !thread.key || selectionMode || deleteSelectedInProgress || getActiveThreadAction(thread.key)) {
            return;
        }

        setThreadMenuAnchorEl(event.currentTarget);
        setThreadMenuThreadId(thread.key);
    };

    const copyShareUrlToClipboard = async (shareUrl, {
        successMessage,
        unsupportedMessage,
        failureMessage,
    }) => {
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
            setPopupMessage(unsupportedMessage);
            return;
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            setPopupMessage(successMessage);
        } catch (copyErr) {
            console.log(copyErr);
            setPopupMessage(failureMessage);
        }
    };

    const shareThread = async (thread) => {
        if (!thread.key || getActiveThreadAction(thread.key)) {
            return;
        }

        setPopupMessage('');
        setActiveThreadAction(thread.key, THREAD_ACTION.SHARE);

        try {
            const response = await getChatAdapter(thread.engineVersion).share(thread.threadId);
            const sharePath = response && response.sharePath ? response.sharePath : getThreadSharePath(thread);
            const shareUrl = `${window.location.origin}${sharePath}`;

            updateThread(thread.key, (prevThread) => ({
                ...prevThread,
                isShared: true,
                sharedAt: response && response.sharedAt ? response.sharedAt : prevThread.sharedAt || new Date().toISOString(),
                sharePath,
            }));

            await copyShareUrlToClipboard(shareUrl, {
                successMessage: 'Link copied to clipboard!',
                unsupportedMessage: 'Thread shared, but copying the link is not supported on this device.',
                failureMessage: 'Thread shared, but copying the link failed.',
            });
        } catch (err) {
            console.log(err);
            setPopupMessage(getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error) || 'Unable to share this thread.');
        } finally {
            setActiveThreadAction(thread.key, null);
        }
    };

    const shareThreadClick = (thread) => async (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleThreadMenuClose();
        await shareThread(thread);
    };

    const openConfirmAction = (thread, actionType) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleThreadMenuClose();

        if (!thread.key || deleteSelectedInProgress || getActiveThreadAction(thread.key)) {
            return;
        }

        setConfirmAction({
            threadId: thread.key,
            threadTitle: getThreadTitle(thread),
            type: actionType,
        });
    };

    const confirmThreadActionClick = async () => {
        if (!confirmAction || !confirmAction.threadId || !confirmAction.type) {
            return;
        }

        const { threadId, type } = confirmAction;
        setConfirmAction(null);
        setPopupMessage('');
        setActiveThreadAction(threadId, type);

        try {
            if (type === THREAD_ACTION.DELETE) {
                await deleteThread(threadId);
                setThreads((prevThreads) => prevThreads.filter((thread) => thread.key !== threadId));
                setSelectedThreadIds((prevSelectedThreadIds) => {
                    if (!prevSelectedThreadIds.has(threadId)) {
                        return prevSelectedThreadIds;
                    }

                    const nextSelectedThreadIds = new Set(prevSelectedThreadIds);
                    nextSelectedThreadIds.delete(threadId);
                    return nextSelectedThreadIds;
                });
            }

            if (type === THREAD_ACTION.UNSHARE) {
                const response = await getChatAdapter(threadForKey(threadId).engineVersion).unshare(threadForKey(threadId).threadId);

                updateThread(threadId, (prevThread) => ({
                    ...prevThread,
                    isShared: false,
                    sharedAt: null,
                    sharePath: response && response.sharePath ? response.sharePath : getThreadSharePath(prevThread),
                }));
                setPopupMessage('Public link disabled.');
            }
        } catch (err) {
            console.log(err);
            const defaultMessage = type === THREAD_ACTION.DELETE ?
                'Unable to delete this thread.' :
                'Unable to stop sharing this thread.';
            setPopupMessage(getChatV2ErrorMessage(err && err.response && err.response.data && err.response.data.error) || defaultMessage);
        } finally {
            setActiveThreadAction(threadId, null);
        }
    };

    const confirmDeleteSelectedThreads = () => {
        if (selectedThreadIds.size === 0) {
            return;
        }

        setShowDeleteSelectedConfirm(true);
    };

    const chatCountText = (count) => count === 1 ? '1 chat' : `${count} chats`;

    const handleDeleteSelectedThreads = async () => {
        const threadIdsToDelete = Array.from(selectedThreadIds);
        if (threadIdsToDelete.length === 0 || deleteSelectedInProgress) {
            return;
        }

        setPopupMessage('');
        setDeleteSelectedInProgress(true);
        setShowDeleteSelectedConfirm(false);

        let deletedCount = 0;
        const failedThreadIds = [];

        for (const threadId of threadIdsToDelete) {
            setActiveThreadAction(threadId, THREAD_ACTION.DELETE);

            try {
                await deleteThread(threadId);
                deletedCount += 1;
                setThreads((prevThreads) => prevThreads.filter((thread) => thread.key !== threadId));
                setSelectedThreadIds((prevSelectedThreadIds) => {
                    const nextSelectedThreadIds = new Set(prevSelectedThreadIds);
                    nextSelectedThreadIds.delete(threadId);
                    return nextSelectedThreadIds;
                });
            } catch (err) {
                console.log(err);
                failedThreadIds.push(threadId);
            } finally {
                setActiveThreadAction(threadId, null);
            }
        }

        if (failedThreadIds.length === 0) {
            setSelectedThreadIds(new Set());
            setSelectionMode(false);
        } else {
            setSelectedThreadIds(new Set(failedThreadIds));
        }

        setDeleteSelectedInProgress(false);

        if (failedThreadIds.length === 0) {
            setPopupMessage(`Deleted ${chatCountText(deletedCount)}.`);
        } else {
            setPopupMessage(`Deleted ${chatCountText(deletedCount)}. Failed to delete ${chatCountText(failedThreadIds.length)}.`);
        }
    };

    const confirmDialogMessage = confirmAction && confirmAction.type === THREAD_ACTION.DELETE ?
        'This removes the conversation and its public link.' :
        'Stop sharing this thread? Existing public links will stop working.';

    const confirmDialogButtonLabel = confirmAction && confirmAction.type === THREAD_ACTION.DELETE ? 'Delete' : 'Stop sharing';
    const confirmDialogTitle = confirmAction && confirmAction.type === THREAD_ACTION.DELETE ?
        'Delete this thread?' :
        confirmAction && confirmAction.threadTitle;
    const bulkDeleteTitle = selectedCount === 1 ? 'Delete chat?' : `Delete ${selectedCount} chats?`;
    const bulkDeleteMessage = selectedCount === 1 ?
        'This will permanently remove the selected chat from your history.' :
        `This will permanently remove the selected ${selectedCount} chats from your history.`;

    if (authenticated !== true) {
        return <SigninRequired authenticated={authenticated} />;
    }

    return (
        <div className={`container appScreen ${selectionMode ? '' : 'hasBottomMenu'}`.trim()}>
            {!selectionMode && <BottomMenu />}
            <div>
                <div className='chatThreadsHeader'>
                    <span className='largeHeading'>History</span>
                    <div className='chatThreadsHeaderActions'>
                        {selectionMode ? (
                            <BlurPillButton
                                className='pageHeaderActionButton'
                                onClick={toggleSelectionMode}
                                disabled={editControlsDisabled}
                                label='Cancel'
                            />
                        ) : (
                            hasThreads && (
                                <BlurPillButton
                                    className='pageHeaderActionButton'
                                    onClick={toggleSelectionMode}
                                    disabled={editControlsDisabled}
                                    label='Select'
                                />
                            )
                        )}
                    </div>
                </div>

                {loading && threads.length === 0 && !hasMore &&
                    <div style={{ textAlign: 'center', padding: '0px 20px' }}>
                        <CircularProgress size={24} aria-label='Loading chat history' />
                    </div>
                }

                {!!errorMessage && !loading && !hasMore && (
                    <div role='alert' style={{ padding: '0px 20px 20px', color: 'var(--app-danger)', fontSize: '14px' }}>
                        {errorMessage}
                        <BlurPillButton label='Retry' onClick={() => tryLoadMore(null)} />
                    </div>
                )}

                {threads.length === 0 && !loading && !errorMessage && !hasMore &&
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--app-text-secondary)' }}>
                        <div style={{ marginBottom: '10px' }}>
                            <IconSmall />
                        </div>
                        <button onClick={() => navigate('/ask')} style={{ fontSize: '15px', fontWeight: '500' }}>
                            Start a new chat.
                        </button>
                    </div>
                }

                <div style={{ padding: '0px 20px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {threads.map((thread) => {
                        const title = getThreadTitle(thread);
                        const activeThreadAction = getActiveThreadAction(thread.key);
                        const threadTopicsText = getThreadTopicsText(thread.lastTopics);
                        const rowMenuOpen = threadMenuThreadId === thread.key;
                        const isPerformingAction = Boolean(activeThreadAction);
                        const isSelected = selectedThreadIds.has(thread.key);
                        const rowClassName = [
                            'chatThreadRow',
                            selectionMode ? 'chatThreadRowSelectable' : '',
                            selectionMode && isSelected ? 'chatThreadRowSelected' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div key={thread.key} className={rowClassName}>
                                <button
                                    onClick={threadClick(thread)}
                                    className='chatThreadOpenButton clampSafeButton'
                                    disabled={isPerformingAction || deleteSelectedInProgress}
                                >
                                    <div className='chatThreadContent'>
                                        <div className='chatThreadTitle max2lines'>
                                            {title}
                                        </div>

                                        {(!!threadTopicsText || thread.isShared) && (
                                            <div className='chatThreadMetadata'>
                                                {!!threadTopicsText && (
                                                    <div className='chatThreadTopics max2lines'>
                                                        {threadTopicsText}
                                                    </div>
                                                )}
                                                {thread.isShared && (
                                                    <div className='chatThreadStatus chatThreadStatusShared'>
                                                        <PublicRounded className='chatThreadStatusIcon' style={{ fontSize: '14px' }} />
                                                        <span>Shared</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {selectionMode && (
                                        <span
                                            className={isSelected ? 'chatThreadSelectionIndicator chatThreadSelectionIndicatorSelected' : 'chatThreadSelectionIndicator'}
                                            aria-hidden='true'
                                        >
                                            {isSelected ? (
                                                <CheckCircleRounded style={{ fontSize: '32px' }} />
                                            ) : (
                                                <RadioButtonUncheckedRounded style={{ fontSize: '32px' }} />
                                            )}
                                        </span>
                                    )}
                                </button>

                                {!selectionMode && (
                                    <div className='chatThreadMenuSlot'>
                                        {isPerformingAction ? (
                                            <div className='chatThreadRowProgress'>
                                                <CircularProgress size={16} thickness={5} color='inherit' />
                                            </div>
                                        ) : (
                                            <>
                                                <button
                                                    type='button'
                                                    onClick={openThreadMenu(thread)}
                                                    className='chatThreadMenuButton'
                                                    disabled={deleteSelectedInProgress}
                                                    aria-label='More options'
                                                    aria-controls={rowMenuOpen ? `chat-thread-menu-${thread.key}` : undefined}
                                                    aria-haspopup='menu'
                                                    aria-expanded={rowMenuOpen ? 'true' : undefined}
                                                >
                                                    <MoreHorizRounded style={{ fontSize: '20px' }} />
                                                </button>
                                                <Menu
                                                    id={`chat-thread-menu-${thread.key}`}
                                                    anchorEl={threadMenuAnchorEl}
                                                    open={rowMenuOpen}
                                                    onClose={handleThreadMenuClose}
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                >
                                                    <MenuItem onClick={shareThreadClick(thread)} disabled={Boolean(activeThreadAction)}>
                                                        <ListItemIcon>
                                                            <IosShare fontSize='small' />
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            {thread.isShared ? 'Share Link' : 'Share'}
                                                        </ListItemText>
                                                    </MenuItem>

                                                    {thread.isShared && (
                                                        <MenuItem onClick={openConfirmAction(thread, THREAD_ACTION.UNSHARE)} disabled={Boolean(activeThreadAction)}>
                                                            <ListItemIcon>
                                                                <LockOutlined fontSize='small' />
                                                            </ListItemIcon>
                                                            <ListItemText>
                                                                Stop Sharing
                                                            </ListItemText>
                                                        </MenuItem>
                                                    )}

                                                    <MenuItem
                                                        onClick={openConfirmAction(thread, THREAD_ACTION.DELETE)}
                                                        disabled={Boolean(activeThreadAction)}
                                                        className='chatThreadMenuItemDanger'
                                                    >
                                                        <ListItemIcon>
                                                            <DeleteOutline fontSize='small' />
                                                        </ListItemIcon>
                                                        <ListItemText>
                                                            Delete
                                                        </ListItemText>
                                                    </MenuItem>
                                                </Menu>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {selectionMode && (
                    <>
                        <div className='savedSelectionBottomSpacer' />
                        <div className='savedSelectionBottomBar chatThreadsSelectionBottomBar'>
                            <div className='savedSelectionCount'>
                                Selected {selectedCount}
                            </div>
                            <button
                                className='savedSelectionDeleteButton'
                                onClick={confirmDeleteSelectedThreads}
                                disabled={selectedCount === 0 || deleteSelectedInProgress}
                            >
                                {deleteSelectedInProgress ? (
                                    <CircularProgress size={18} thickness={5} color='inherit' />
                                ) : (
                                    <DeleteOutline style={{ fontSize: '19px', verticalAlign: 'middle' }} />
                                )}
                                <span>{deleteSelectedInProgress ? 'Deleting...' : 'Delete'}</span>
                            </button>
                        </div>
                    </>
                )}

                {hasMore && (
                    <div ref={loadMoreObserverRef} style={{ textAlign: 'center', minHeight: '44px', padding: '12px 20px' }}>
                        {loading && <CircularProgress size={24} aria-label='Loading more chats' />}
                        {!!errorMessage && !loading && <div role='alert'>
                            <div>{errorMessage}</div>
                            <BlurPillButton label='Retry' onClick={() => tryLoadMore(cursor)} />
                        </div>}
                    </div>
                )}
            </div>

            <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
                <DialogContent>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                        {confirmDialogTitle}
                    </div>
                    <div>
                        {confirmDialogMessage}
                    </div>
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setConfirmAction(null)} style={{ padding: '8px 14px' }}>
                        Cancel
                    </button>
                    <button onClick={confirmThreadActionClick} style={{ padding: '8px 14px' }}>
                        {confirmDialogButtonLabel}
                    </button>
                </DialogActions>
            </Dialog>

            <Dialog open={showDeleteSelectedConfirm} onClose={() => !deleteSelectedInProgress && setShowDeleteSelectedConfirm(false)}>
                <DialogContent>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                        {deleteSelectedInProgress ? 'Deleting...' : bulkDeleteTitle}
                    </div>
                    <div>
                        {bulkDeleteMessage}
                    </div>
                </DialogContent>
                <DialogActions>
                    <button
                        onClick={() => setShowDeleteSelectedConfirm(false)}
                        disabled={deleteSelectedInProgress}
                        style={{ padding: '8px 14px' }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDeleteSelectedThreads}
                        disabled={deleteSelectedInProgress || selectedCount === 0}
                        style={{ padding: '8px 14px', color: 'var(--app-danger)' }}
                    >
                        Delete
                    </button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!popupMessage} onClose={() => setPopupMessage('')}>
                <DialogContent>
                    {popupMessage}
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setPopupMessage('')} style={{ padding: '8px 14px' }}>
                        OK
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

ChatThreads.propTypes = {
    authenticated: PropTypes.bool,
};

ChatThreads.defaultProps = {

};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
});

const AccountChatThreads = (props) => <ChatThreads key={props.uid || 'guest'} {...props} />;

export default connect(mapStateToProps)(AccountChatThreads);
